/**
 * Conversion helpers between the base64url strings our GraphQL API deals in
 * and the binary shapes the Push API works with.
 *
 * `PushManager.subscribe`'s `applicationServerKey` option takes binary (a
 * `BufferSource`), but `pushConfig.vapidPublicKey` is handed out as
 * base64url. Conversely, `PushSubscription.getKey('p256dh' | 'auth')`
 * returns an `ArrayBuffer`, but the `registerPushSubscription` mutation and
 * the `web-push` server library both expect base64url strings.
 */

/** base64url string -> raw bytes, for `applicationServerKey`. */
export const urlBase64ToUint8Array = (
  base64String: string
): Uint8Array<ArrayBuffer> => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

/** raw bytes -> base64url string (no padding, `-`/`_`), for `p256dh`/`auth`. */
export const arrayBufferToBase64Url = (
  buffer: ArrayBufferLike | null
): string => {
  if (!buffer) return '';

  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

// The VAPID key each subscription was created with, recorded at subscribe()
// time and keyed by endpoint — same localStorage convention as
// the device id in `WebPushHandler`. Needed because the browser is allowed to withhold
// `options.applicationServerKey` (nullable by spec; Brave returns null as
// fingerprinting resistance), and *we* are the only other party who knows
// which key the subscription actually uses. Losing an entry is harmless:
// the null-key path in `WebPushHandler` degrades to "reuse and re-record".
const SERVER_KEY_STORAGE_PREFIX = 'BILDERIT_PUSH_SERVER_KEY:';

export const readRecordedServerKey = (endpoint: string): string | null =>
  localStorage.getItem(`${SERVER_KEY_STORAGE_PREFIX}${endpoint}`);

export const recordServerKey = (
  endpoint: string,
  base64UrlKey: string
): void => {
  localStorage.setItem(`${SERVER_KEY_STORAGE_PREFIX}${endpoint}`, base64UrlKey);
};
